/*
  Custom Cart Callbacks for Rebuy Smart Cart
 
  This file handles dynamic UI updates for cart items, when discount codes are applied or removed.
  - Tracks the original state of cart items
  - Calculates correct prices when discounts are applied using proportional allocation
  - Updates the DOM to display discounted prices with proper formatting
  - Reverts items to their original state when discounts are removed
  - Responds to cart changes, drawer opening, and discount code application events
 
  Relies on window.RebuyUtils for cart data and discount allocation calculations.
 */

window.RebuyCartCallbacks = (function () {
  // Private variables
  const originalStates = new Map();

  // Check if a code is a valid 3-for-2 code
  function isValid3For2Code(code) {
    if (!code || !window.active3for2codes?.buyXgetYcodes) return false;
    return window.active3for2codes.buyXgetYcodes.includes(code.trim().toUpperCase());
  }

  // Private methods
  function calculatePrices(cartItem, discountPercent = 0, discountAllocation = null) {
    // Get original state if available
    const originalState = originalStates.get(cartItem.id);

    // Determine if this is a sale item
    const isSaleItem = originalState
      ? originalState.isSaleItem
      : cartItem.product?.selected_variant?.compare_at_price &&
        parseFloat(cartItem.product.selected_variant.compare_at_price) >
          parseFloat(cartItem.price) / 100
      ? true
      : false;

    // Get base price (cents to dollars)
    const salePricePerUnit = originalState
      ? originalState.basePrice
      : parseFloat(cartItem.price) / 100;

    // Get compare-at price if it's a sale item
    const fullPricePerUnit = isSaleItem
      ? cartItem.product?.selected_variant?.compare_at_price || null
      : null;

    // Calculate base total prices
    let totalSalePrice = salePricePerUnit * cartItem.quantity;
    let totalCompareAtPrice = fullPricePerUnit ? fullPricePerUnit * cartItem.quantity : null;

    // Apply discount if active and we have a discount allocation
    if (discountPercent > 0 && discountAllocation) {
      // Use the pre-calculated and properly distributed discounted price
      totalSalePrice = discountAllocation.discountedPrice;
    } else if (discountPercent > 0) {
      // Fallback to simple percentage calculation if no allocation provided
      totalSalePrice = totalSalePrice * (1 - discountPercent / 100);
      totalSalePrice = Math.round(totalSalePrice * 100) / 100;
    }

    return {
      totalSalePrice: totalSalePrice.toFixed(2).replace('.', ','),
      totalCompareAtPrice: totalCompareAtPrice
        ? totalCompareAtPrice.toFixed(2).replace('.', ',')
        : null,
      hasDiscount: discountPercent > 0,
      isSaleItem: isSaleItem,
    };
  }

  function saveOriginalState(cartItem, itemElement, isSaleItem = null) {
    // Only save if we haven't already
    if (!originalStates.has(cartItem.id)) {
      const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
      if (!priceContainer) {
        console.error(`[ERROR] No price container found for item ${cartItem.id}`);
        return;
      }

      // Determine if this is a sale item - prefer the passed value or check the DOM
      const itemIsSale =
        isSaleItem !== null
          ? isSaleItem
          : priceContainer.querySelector('.rebuy-money.compare-at') !== null ||
            priceContainer.querySelector('.rebuy-money.sale') !== null;

      originalStates.set(cartItem.id, {
        html: priceContainer.innerHTML, // Save entire price container HTML
        basePrice: parseFloat(cartItem.price) / 100,
        isSaleItem: itemIsSale,
      });
    }
  }

  function updateCartItemDOM(
    cartItem,
    totalSalePrice,
    totalCompareAtPrice,
    hasDiscount,
    isSaleItem
  ) {
    const itemElement = document.querySelector(`[data-item-id="${cartItem.id}"]`);
    if (!itemElement) return;

    const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
    if (!priceContainer) return;

    // Save the original state before making changes
    saveOriginalState(cartItem, itemElement, isSaleItem);

    // First ensure we have proper numbers by parsing any string inputs
    const numericSalePrice =
      typeof totalSalePrice === 'string'
        ? parseFloat(totalSalePrice.replace(',', '.'))
        : totalSalePrice;

    const numericComparePrice =
      totalCompareAtPrice && typeof totalCompareAtPrice === 'string'
        ? parseFloat(totalCompareAtPrice.replace(',', '.'))
        : totalCompareAtPrice;

    // Format with periods first, then replace with commas for display
    const formattedSalePrice = numericSalePrice.toFixed(2).replace('.', ',');
    const formattedComparePrice = numericComparePrice
      ? numericComparePrice.toFixed(2).replace('.', ',')
      : null;

    // Different HTML structure based on discount and compare-at price
    if (hasDiscount) {
      // With discount applied - always show as sale item with compare-at
      let compareAtPrice;

      if (isSaleItem) {
        // For items already on sale, compare-at is the original sale price
        const original = originalStates.get(cartItem.id);
        compareAtPrice = (original.basePrice * cartItem.quantity).toFixed(2).replace('.', ',');
      } else {
        // For regular items with discount, compare-at is the regular price
        compareAtPrice = ((parseFloat(cartItem.price) / 100) * cartItem.quantity)
          .toFixed(2)
          .replace('.', ',');
      }

      priceContainer.innerHTML = `
              <div>
                <span class="rebuy-money sale">
                  <span class="sr-only">Discounted Price</span>
                  <span>€ ${formattedSalePrice}</span>
                </span>
                <span class="rebuy-money compare-at">
                  <span class="sr-only">Original Price</span>
                  <span>€ ${compareAtPrice}</span>
                </span>
              </div>
            `;
    } else if (isSaleItem) {
      // Regular sale item without discount code
      priceContainer.innerHTML = `
              <div>
                <span class="rebuy-money sale">
                  <span class="sr-only">Sale Price</span>
                  <span>€ ${formattedSalePrice}</span>
                </span>
                <span class="rebuy-money compare-at">
                  <span class="sr-only">Compare At Price</span>
                  <span>€ ${formattedComparePrice}</span>
                </span>
              </div>
            `;
    } else {
      // Regular non-sale item
      priceContainer.innerHTML = `
              <span class="rebuy-money">
                <span class="sr-only">Price</span>
                <span>€ ${formattedSalePrice}</span>
              </span>
            `;
    }
  }

  function revertItemToOriginal(cartItem, isSaleItem) {
    const itemId = cartItem.id;
    const original = originalStates.get(itemId);

    if (original) {
      const itemElement = document.querySelector(`[data-item-id="${itemId}"]`);
      if (!itemElement) return;

      const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
      if (priceContainer) {
        // Handle quantity changes - need to update prices based on new quantity
        const currentQty = cartItem.quantity;

        // Use provided isSaleItem if available, otherwise fall back to original state
        const itemIsSale = isSaleItem !== undefined ? isSaleItem : original.isSaleItem;

        if (itemIsSale) {
          // Was originally a sale item
          const salePrice = (original.basePrice * currentQty).toFixed(2).replace('.', ',');
          const compareAtPrice = cartItem.product?.selected_variant?.compare_at_price
            ? (cartItem.product.selected_variant.compare_at_price * currentQty)
                .toFixed(2)
                .replace('.', ',')
            : null;

          if (compareAtPrice) {
            priceContainer.innerHTML = `
                          <div>
                            <span class="rebuy-money sale">
                              <span class="sr-only">Sale Price</span>
                              <span>€ ${salePrice}</span>
                            </span>
                            <span class="rebuy-money compare-at">
                              <span class="sr-only">Compare At Price</span>
                              <span>€ ${compareAtPrice}</span>
                            </span>
                          </div>
                        `;
          } else {
            priceContainer.innerHTML = `
                          <span class="rebuy-money">
                            <span class="sr-only">Price</span>
                            <span>€ ${salePrice}</span>
                          </span>
                        `;
          }
        } else {
          // Was originally a regular item
          const price = (original.basePrice * currentQty).toFixed(2).replace('.', ',');
          priceContainer.innerHTML = `
                        <span class="rebuy-money">
                          <span class="sr-only">Price</span>
                          <span>€ ${price}</span>
                        </span>
                      `;
        }
      } else {
        console.error(`[ERROR] No price container found for Item ID: ${itemId}`);
      }
    }
  }

  async function processCartItems(cart) {
    if (!cart.items.length) return;

    try {
      // Get the active discount from the utils
      const currentDiscount = window.RebuyUtils.getActiveDiscount();
      const discountPercent = Math.abs(currentDiscount);

      // Check if this is a 3-for-2 code
      const discountTag = document.querySelector('.rebuy-cart__discount-tag');
      const tagCode =
        discountTag && discountTag.offsetParent !== null
          ? discountTag.textContent.trim().split(' ')[0].toUpperCase()
          : '';
      const is3For2Code = isValid3For2Code(tagCode);

      if (is3For2Code) {
        console.log('[RebuyCartCallbacks] 3-for-2 code active, using minimal processing');

        // Process items that aren't handled by ThreeForTwoHandler
        cart.items.forEach(cartItem => {
          try {
            // Skip if item is a free gift
            if (parseInt(cartItem.price) === 0) {
              return;
            }

            // Skip if this item is being handled by ThreeForTwoHandler
            const itemElement = document.querySelector(`[data-item-id="${cartItem.id}"]`);
            if (itemElement && itemElement.hasAttribute('data-three-for-two-free')) {
              return; // This item is being managed by ThreeForTwoHandler
            }

            // Handle regular items normally
            const isSaleItem =
              cartItem.product?.selected_variant?.compare_at_price &&
              parseFloat(cartItem.product.selected_variant.compare_at_price) >
                parseFloat(cartItem.price) / 100;

            if (originalStates.has(cartItem.id)) {
              revertItemToOriginal(cartItem, isSaleItem);
            } else {
              // Calculate prices
              const { totalSalePrice, totalCompareAtPrice } = calculatePrices(cartItem);

              // Display regular prices
              updateCartItemDOM(
                cartItem,
                totalSalePrice,
                isSaleItem
                  ? (
                      cartItem.product.selected_variant.compare_at_price * cartItem.quantity
                    ).toFixed(2)
                  : null,
                false, // No discount
                isSaleItem
              );
            }
          } catch (error) {
            console.error(`Error processing cart item ${cartItem.id}:`, error);
          }
        });

        return; // Skip further processing
      }

      // Get subtotal for single-item cart consistency
      let exactSubtotal = null;
      if (cart.items.length === 1) {
        const subtotalElement = document.querySelector('.rebuy-cart__flyout-subtotal-final-amount');
        if (subtotalElement) {
          const subtotalText = subtotalElement.textContent.trim();
          const subtotalMatch = subtotalText.match(/[\d.,]+/);
          if (subtotalMatch) {
            // Properly handle comma as decimal separator
            // First extract the numeric part, preserving commas
            const numericText = subtotalMatch[0];
            // Then replace comma with period for proper parsing
            const parsableText = numericText.replace(',', '.');
            // Parse to a proper number
            exactSubtotal = parseFloat(parsableText);
          }
        }
      }

      if (currentDiscount < 0) {
        // Discount is active - use the discount allocator from utils
        const { allocations } = window.RebuyUtils.allocateDiscountAcrossItems(
          cart.items,
          discountPercent
        );

        // Process each item with its allocated discount
        cart.items.forEach(cartItem => {
          try {
            if (parseInt(cartItem.price) === 0) {
              // This is likely a free gift, skip UI updates
              return;
            }

            // Check directly from product variant data
            const isSaleItem =
              cartItem.product?.selected_variant?.compare_at_price &&
              parseFloat(cartItem.product.selected_variant.compare_at_price) >
                parseFloat(cartItem.price) / 100;

            // Save original state if it doesn't exist
            const itemElement = document.querySelector(`[data-item-id="${cartItem.id}"]`);
            if (itemElement) {
              saveOriginalState(cartItem, itemElement, isSaleItem);
            }

            // Get the discount allocation for this item
            const allocation = allocations.get(cartItem.id);
            if (!allocation) {
              console.error(`[ERROR] No discount allocation found for item ${cartItem.id}`);
              return;
            }

            // Get original state to determine if it's a sale item
            const original = originalStates.get(cartItem.id);

            // Format the discounted price as a string with 2 decimal places
            let discountedPriceDisplay;

            // For single-item carts, use exact subtotal if available
            if (cart.items.length === 1 && exactSubtotal !== null) {
              // Format with comma as decimal separator for European markets
              discountedPriceDisplay = exactSubtotal.toFixed(2).replace('.', ',');
            } else {
              // For multiple items, also format with comma
              discountedPriceDisplay = allocation.discountedPrice.toFixed(2).replace('.', ',');
            }

            // Calculate appropriate compare-at price
            let compareAtPrice;
            if (isSaleItem) {
              // For items already on sale, compare-at is the original sale price
              compareAtPrice = original
                ? (original.basePrice * cartItem.quantity).toFixed(2).replace('.', ',')
                : null;
            } else {
              // For regular items with discount, compare-at is the regular price
              compareAtPrice = allocation.originalPrice.toFixed(2).replace('.', ',');
            }

            updateCartItemDOM(cartItem, discountedPriceDisplay, compareAtPrice, true, isSaleItem);
          } catch (error) {
            console.error(`Error processing cart item ${cartItem.id}:`, error);
          }
        });
      } else {
        cart.items.forEach(cartItem => {
          try {
            // Skip if item is a free gift
            if (parseInt(cartItem.price) === 0) {
              return;
            }

            // IMPROVED SALE DETECTION: Check directly from product variant data
            const isSaleItem =
              cartItem.product?.selected_variant?.compare_at_price &&
              parseFloat(cartItem.product.selected_variant.compare_at_price) >
                parseFloat(cartItem.price) / 100;

            if (originalStates.has(cartItem.id)) {
              revertItemToOriginal(cartItem, isSaleItem);
            } else {
              // Calculate prices (ensuring we correctly identify sale items)
              const { totalSalePrice, totalCompareAtPrice, hasDiscount } =
                calculatePrices(cartItem);

              // Force the correct display based on product data
              updateCartItemDOM(
                cartItem,
                totalSalePrice,
                isSaleItem
                  ? (cartItem.product.selected_variant.compare_at_price * cartItem.quantity)
                      .toFixed(2)
                      .replace('.', ',')
                  : null,
                hasDiscount,
                isSaleItem // Use the directly detected sale status
              );
            }
          } catch (error) {
            console.error(`Error processing cart item ${cartItem.id}:`, error);
          }
        });
      }
    } catch (error) {
      console.error('Error in processCartItems:', error);
    }
  }

  // Public API
  return {
    // Only expose the init function
    init: function () {
      // Check if EventManager is available
      if (!window.RebuyEventManager) {
        console.error('[RebuyCartCallbacks] ERROR: RebuyEventManager is not available!');
        return;
      }

      // Register for all relevant events
      window.RebuyEventManager.on('cart:change', data => {
        processCartItems(data.cart);
      })
        .on('cart:ready', data => {
          processCartItems(data.cart);
        })
        .on('cart:show', data => {
          processCartItems(data.cart);
        })
        .on('discount:applied', data => processCartItems(data.cart))
        .on('discount:removed', data => processCartItems(data.cart));
    },
  };
})();
